import type { Api } from '@technology/web3d/dist/esnext/Api.js';
import type { SnapPick } from '@technology/web3d/dist/esnext/common.js';
import type { PointerObservableOptions } from '@technology/web3d/dist/esnext/InputHandler.js';
import { Tool } from '@technology/web3d/dist/esnext/Tools/Tool.js';
import type { Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import {
  createDefaultTextObject,
  createPreviewTextObject,
  TEXT_CREATION_TOOL_NAME,
} from './text-creation-defaults';
import type { TextEditingModel } from './text-editing-model';
import { TextMesh } from './text-mesh';

/** Matches MouseButton.left; avoids ambient const-enum under isolatedModules. */
const LEFT_MOUSE_BUTTON = 0;

export class TextCreationTool extends Tool {
  public readonly name = TEXT_CREATION_TOOL_NAME;

  private _enabled = false;
  private _clickHandle: Subscription | undefined;
  private _model: TextEditingModel;
  private _previewMesh: TextMesh | undefined;

  constructor(
    private readonly _api: Api,
    model: TextEditingModel
  ) {
    super();
    this._model = model;
  }

  public setModel(model: TextEditingModel): void {
    this._model = model;
  }

  public set enabled(enabled: boolean) {
    if (enabled === this._enabled) {
      return;
    }

    if (enabled) {
      this._api.cursor.subscribe(() => {
        this.onCursorMove();
      });
      const options: PointerObservableOptions = {
        button: LEFT_MOUSE_BUTTON,
        touchCount: 1,
      };
      const clickObservable =
        this._api.inputHandler.createTapObservable(options);
      this._clickHandle = clickObservable.subscribe({
        next: (event) => {
          void this._api.cursor
            .calculateIntersection(event)
            .then(() => {
              const intersection = this._api.cursor.intersection;
              if (intersection) {
                this.onClick(intersection);
              }
            })
            .catch((error: unknown) => {
              // Keep placement preview alive; surface picker outages in the console.
              console.warn(
                'TextCreationTool: calculateIntersection failed',
                error
              );
            });
        },
      });
      this._enabled = true;
      this.onEnabled();
    } else {
      this._api.cursor.unsubscribe();
      this._clickHandle?.unsubscribe();
      this._clickHandle = undefined;
      this._enabled = false;
      this.onDisabled();
    }
  }

  public get enabled(): boolean {
    return this._enabled;
  }

  private onEnabled(): void {
    this._previewMesh = new TextMesh(createPreviewTextObject());
    this._model.attachPreviewMesh(this._previewMesh);
  }

  private onDisabled(): void {
    if (this._previewMesh) {
      this._model.detachPreviewMesh(this._previewMesh);
      this._previewMesh = undefined;
    }
  }

  private onCursorMove(): void {
    const intersection = this._api.cursor.intersection;
    const point = intersection?.point;
    if (!point || !this._previewMesh) {
      return;
    }

    this._previewMesh.setInsertionPointPreview(point);
    this._api.renderingManager.redraw();
  }

  private onClick(pick: SnapPick): void {
    const point = pick.point;
    if (!point) {
      return;
    }

    const textObject = createDefaultTextObject(
      {
        x: point.x,
        y: point.y,
        z: point.z,
      },
      uuidv4()
    );
    this._model.addText(textObject);
    this._api.renderingManager.redraw();
  }
}
